﻿import { getErrorMessage, isErrorResponse, isAxiosError } from "../../lib/status-codes";
import { validationSummary } from "../../lib/validations";
import axios from "axios";
import { LoadingSpinner } from "../../components/loadingSpinner";
import { SdfFileList } from "../../components/sdf-file-list";
import { ListRowItem } from "../../components/sdf-file-list/types";
import { getElement, downloadFile } from "../../lib/elementUtils";
import { getTranslations, Translations } from "../../lib/translations";
import { currentPageWithHandler } from "../../lib/urlUtil";
import { DataTablesHelper } from "../../lib/datatables";
import { addToGTMDataLayer } from "../../lib/gtm/index";
class Sdf {
    private domNodes = {
        parent: getElement(document, ".sdf"),
        buttons: {
            download: getElement<HTMLInputElement>(document, ".sdf__button--download"),
            downloadRecentFiles: document.querySelector<HTMLInputElement>(".sdf-recent-files__button--download"),
            refresh: getElement<HTMLInputElement>(document, ".sdf__button--refresh")
        },
        fileListForm: getElement<HTMLFormElement>(document, ".sdf__form"),
        fileList: getElement(document, ".sdf__file-list", "File list"),
        recentFilesList: document.querySelector<HTMLElement>(".sdf-recent-files")
    }

    private translations?: Translations;

    private fileListSpinner: LoadingSpinner;

    private recentFileListSpinner?: LoadingSpinner;

    private fileList: SdfFileList;

    private recentFileList?: SdfFileList;

    constructor() {
        // Add configuration to make SDF File Archive table as a datatable
        DataTablesHelper.SoftwareDevelopmentForum();

        // Add configuration to make SDF New Files table as a datatable
        DataTablesHelper.SoftwareDevelopmentForumRecentFiles();

        this.addEventListeners();

        this.fileListSpinner = new LoadingSpinner(this.domNodes.fileListForm, "Loading");

        if (this.domNodes.recentFilesList) {
            this.recentFileListSpinner = new LoadingSpinner(this.domNodes.recentFilesList, "Loading")
        }

        getTranslations().then(translations => {
            this.translations = translations
            this.fileListSpinner.message = this.translations.Loading
            if (this.recentFileListSpinner) {
                this.recentFileListSpinner.message = this.translations.Loading
            }
        })

        this.fileList = new SdfFileList(getElement(document, ".sdf__file-list"), true);

        if (this.domNodes.recentFilesList) {
            this.recentFileList = new SdfFileList(this.domNodes.recentFilesList, false)
        }
    }

    /**
     * Add all page event listeners
     */
    private addEventListeners() {
        this.domNodes.fileListForm.addEventListener("submit", (ev) => {
            ev.preventDefault();
        });

        this.domNodes.buttons.download.addEventListener("click", async () => {
            // Download selected files
            await this.downloadFiles();
        });

        this.domNodes.buttons.refresh.addEventListener("click", async () => {
            // Refresh the file list
            validationSummary();
            await this.refreshFileList();
        });

        this.domNodes.buttons.downloadRecentFiles?.addEventListener("click", async () => {
            // Download any selected recent files
            validationSummary();
            await this.downloadRecentFiles();
        });

    }

    /**
     * Download the selected files
     */
    private async downloadFiles() {
        const selectedFiles = this.fileList.getSelectedItems()
        const formData = new FormData(this.domNodes.fileListForm);

        await this.downloadSdfFiles(selectedFiles, formData, this.fileListSpinner, this.translations);
    }

    /**
     * Download any selected recently uploaded files
     */
    private async downloadRecentFiles() {
        if (!this.recentFileList) {
            throw new Error("Recent file list not present on the page");
        }

        const selectedFiles = this.recentFileList.getSelectedItems();
        const formData = new FormData(this.domNodes.fileListForm);

        await this.downloadSdfFiles(selectedFiles, formData, this.recentFileListSpinner!, this.translations);
    }

    /**
     *  Download the currently selected list of files.
     *  UI updates are handled and errors will be shown to the user.
     *
     * @export
     * @param {GenericFileInput[]} selectedFiles A list of filenames that should be downloaded
     * @param {FormData} formData The FormData from the form that is being submitted. This is sent along with the post request
     * @param {LoadingSpinner} loadingSpinner The loading spinner to be shown while the request takes place
     * @param {Translations} [translations] The translations to be used for any messages shown to the user
     * @returns
     */
    private async downloadSdfFiles(selectedFiles: ListRowItem[], formData: FormData, loadingSpinner: LoadingSpinner, translations?: Translations) {
        validationSummary();
        formData.append("fileSelectionString", JSON.stringify(selectedFiles))
        if (selectedFiles.length === 0) {
            validationSummary({
                body: translations?.No_Files_Selected ?? "Please select a file to download",
                level: "error",
                title: ""
            });

            addToGTMDataLayer(translations?.No_Files_Selected ?? "Please select a file to download", "dewi_error_context", "SDF file download");
            return;
        }

        await loadingSpinner.startSpinner()
        try {
            const response = await axios.post("?handler=DownloadFiles", formData, {
                responseType: "blob"
            });
            if (isErrorResponse(response)) {
                throw response;
            }
            await downloadFile(response);

        } catch (error: any) {
            const errorMessage = isAxiosError(error) ? `${error.response?.status}: ${translations?.ErrorTitle
                } ` : translations?.ErrorTitle ?? "An error occurred while downloading files";
            console.error({ error, errorMessage });

            validationSummary({
                level: "error",
                body: errorMessage,
                title: ""
            });

            addToGTMDataLayer(errorMessage, "dewi_error_context", "SDF download error");
        } finally {
            loadingSpinner.stopSpinner()
        }
    }

    /**
     * Refresh the files list
     */
    private async refreshFileList() {
        const refreshFileListUrl = currentPageWithHandler("FileListPartial");

        await this.fileListSpinner.startSpinner();
        try {
            const response = await axios.get(refreshFileListUrl);
            if (isErrorResponse(response)) {
                throw response;
            }
            this.domNodes.fileList.innerHTML = response.data;
            this.fileList.addEventListeners();

        } catch (error: any) {
            console.error(error);
            validationSummary();
            validationSummary({
                title: "",
                body: await getErrorMessage(error),
                level: "error"
            });
        } finally {
            DataTablesHelper.SoftwareDevelopmentForum();
            await this.fileListSpinner.stopSpinner();
        }
    }

}

/**
 * Initialize the page
 */
async function init() {
    const page = new Sdf();
}

init();
